import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check for existing authentication on app load
    checkAuthStatus();
  }, []);

  const checkAuthStatus = () => {
    try {
      const storedToken = sessionStorage.getItem('id_token');
      const storedUser = sessionStorage.getItem('user_info');

      if (storedToken && storedUser) {
        const userInfo = JSON.parse(storedUser);
        
        // Check if token is still valid (basic check)
        if (isTokenValid(storedToken)) {
          setToken(storedToken);
          setUser(userInfo);
        } else {
          // Token expired, clear storage
          signOut();
        }
      }
    } catch (error) {
      console.error('Error checking auth status:', error);
      signOut();
    } finally {
      setIsLoading(false);
    }
  };

  const isTokenValid = (token) => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;
      
      // Check if token is expired
      return payload.exp > currentTime;
    } catch (error) {
      console.error('Error validating token:', error);
      return false;
    }
  };

  const signIn = (userInfo, idToken) => {
    setUser(userInfo);
    setToken(idToken);
  };

  const signOut = () => {
    // Clear session storage
    sessionStorage.removeItem('id_token');
    sessionStorage.removeItem('user_info');
    
    // Clear state
    setUser(null);
    setToken(null);
    
    // Sign out from Google
    if (window.google && window.google.accounts) {
      window.google.accounts.id.disableAutoSelect();
    }
  };

  const getAuthHeaders = () => {
    if (!token) return {};
    
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  const value = {
    user,
    token,
    isLoading,
    isAuthenticated: !!user && !!token,
    signIn,
    signOut,
    getAuthHeaders
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
